// Admin Garments list. Each card has inline actions: open detail, rename
// (inline edit), enable/disable (PATCH isActive), and delete (hard if no
// orders attached, falls back to disable). New garment via the modal.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'
import { AI_TOOLS_ENABLED } from '../../lib/features'

interface Garment {
  id: string; slug: string; name: string; description: string | null
  isActive: boolean; sortOrder: number
  aiPreviewEnabled: boolean
  colourCount: number; variantCount: number
  minPrice: number | null; maxPrice: number | null
  mockupUrl: string | null
}

export default function Garments() {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [items, setItems] = useState<Garment[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ slug: '', name: '', description: '' })
  const [renaming, setRenaming] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  // Active / Disabled / All filter. Default = Active so the customer-facing
  // catalogue is what shows up first; disabled garments are still one click
  // away when an admin needs to audit or re-enable them.
  const [filter, setFilter] = useState<'active' | 'disabled' | 'all'>('active')

  async function load() {
    try {
      const d = await apiFetch<{ items: Garment[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/garments')
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [])

  async function create() {
    if (!draft.slug || !draft.name) { alert('Slug + name required'); return }
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/garments', { method: 'POST', body: JSON.stringify(draft) })
      setDraft({ slug: '', name: '', description: '' }); setCreating(false)
      await load()
    } catch (e) { alert((e as Error).message) }
  }

  async function toggleActive(g: Garment) {
    setBusyId(g.id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${g.id}`, {
        method: 'PATCH', body: JSON.stringify({ isActive: !g.isActive }),
      })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusyId(null) }
  }

  async function toggleAiPreview(g: Garment) {
    setBusyId(g.id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${g.id}`, {
        method: 'PATCH', body: JSON.stringify({ aiPreviewEnabled: !g.aiPreviewEnabled }),
      })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusyId(null) }
  }

  async function remove(g: Garment) {
    if (!confirm(`Delete ${g.name}? If this garment has any orders the system will disable it instead of hard-deleting.`)) return
    setBusyId(g.id)
    try {
      const r = await apiFetch<{ ok: boolean; mode: 'hard' | 'disabled'; reason?: string }>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        `/api/admin/garments/${g.id}`, { method: 'DELETE' },
      )
      if (r?.mode === 'disabled') alert(r.reason ?? 'Garment disabled instead of deleted.')
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusyId(null) }
  }

  async function commitRename(g: Garment) {
    const next = renameDraft.trim()
    setRenaming(null)
    if (!next || next === g.name) return
    setBusyId(g.id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${g.id}`, {
        method: 'PATCH', body: JSON.stringify({ name: next }),
      })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusyId(null) }
  }

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Catalog</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Garments</h1>
          <p className="text-ink-2 text-sm mt-1 max-w-2xl">Every garment offered in the public Designer. Add new product types, manage colourways, per-size pricing and print locations. Disable to hide from customers while keeping history intact.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold">+ New garment</button>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      {/* Show/hide active or disabled garments. Counts in each chip so the
          admin can see at a glance how the catalogue splits. */}
      {(() => {
        const activeCount = items.filter((g) => g.isActive).length
        const disabledCount = items.length - activeCount
        const tabs: Array<{ key: 'active' | 'disabled' | 'all'; label: string; count: number }> = [
          { key: 'active',   label: 'Active',   count: activeCount },
          { key: 'disabled', label: 'Disabled', count: disabledCount },
          { key: 'all',      label: 'All',      count: items.length },
        ]
        return (
          <div className="flex flex-wrap gap-1.5 mb-5 border-b border-border pb-1">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setFilter(t.key)}
                className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
                  (filter === t.key ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
                {t.label} <span className="ml-1 text-ink-3">{t.count}</span>
              </button>
            ))}
          </div>
        )
      })()}

      {(() => {
        const visible = filter === 'all' ? items : items.filter((g) => filter === 'active' ? g.isActive : !g.isActive)
        return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="card text-center text-ink-3 text-sm md:col-span-2 lg:col-span-3">No garments yet.</div>
        ) : visible.length === 0 ? (
          <div className="card text-center text-ink-3 text-sm md:col-span-2 lg:col-span-3">No {filter === 'active' ? 'active' : 'disabled'} garments in this view.</div>
        ) : null}
        {visible.map((g) => (
          <div key={g.id} className={'card ' + (g.isActive ? '' : 'opacity-70')}>
            <div className="flex gap-3 items-start">
              <Link to={`/admin/garments/${g.id}`} className="w-20 h-20 bg-surface border border-border rounded overflow-hidden flex items-center justify-center shrink-0 hover:border-gold">
                {g.mockupUrl ? <img src={apiSrc(g.mockupUrl)} alt={g.name} className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] uppercase tracking-wider text-ink-3">{g.slug}</span>}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-ink-3">{g.slug}</div>
                {renaming === g.id ? (
                  <input
                    value={renameDraft}
                    autoFocus
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => commitRename(g)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitRename(g); if (e.key === 'Escape') setRenaming(null) }}
                    className="font-heading uppercase font-bold text-lg text-ink bg-white border border-gold rounded px-1 w-full"
                  />
                ) : (
                  <Link to={`/admin/garments/${g.id}`} className="font-heading uppercase font-bold text-lg text-ink hover:text-gold-dark truncate block">{g.name}</Link>
                )}
                <div className="text-xs text-ink-3 mt-1">{g.colourCount} colours · {g.variantCount} sizes</div>
                {g.minPrice != null && (
                  <div className="text-sm text-ink mt-1">
                    {g.minPrice === g.maxPrice ? `$${g.minPrice.toFixed(2)}` : `$${g.minPrice.toFixed(2)} – $${g.maxPrice!.toFixed(2)}`}
                  </div>
                )}
                {!g.isActive && <span className="inline-block mt-1 mr-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600">Disabled</span>}
                {AI_TOOLS_ENABLED && !g.aiPreviewEnabled && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-700">AI preview off</span>}
              </div>
            </div>

            {/* Inline actions */}
            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border text-xs flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <Link to={`/admin/garments/${g.id}`} className="text-gold-dark hover:underline">Edit details</Link>
                <button disabled={busyId === g.id} onClick={() => { setRenameDraft(g.name); setRenaming(g.id) }} className="text-ink-3 hover:text-ink">Rename</button>
                {AI_TOOLS_ENABLED && (
                  <button disabled={busyId === g.id} onClick={() => toggleAiPreview(g)}
                    title={g.aiPreviewEnabled ? 'Hide the ✨ Preview button from customers for this garment' : 'Show the ✨ Preview button to customers for this garment'}
                    className={(g.aiPreviewEnabled ? 'text-ink-3 hover:text-ink' : 'text-amber-700 hover:text-amber-800 font-semibold')}>
                    {g.aiPreviewEnabled ? 'Disable AI preview' : 'Enable AI preview'}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button disabled={busyId === g.id} onClick={() => toggleActive(g)} className={(g.isActive ? 'text-ink-3 hover:text-ink' : 'text-emerald-700 hover:text-emerald-800 font-semibold')}>
                  {g.isActive ? 'Disable' : 'Enable'}
                </button>
                <button disabled={busyId === g.id} onClick={() => remove(g)} className="text-red-600 hover:text-red-700">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
        )
      })()}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-heading uppercase font-bold text-lg text-ink mb-4">New garment</h3>
            <div className="space-y-3">
              <div><div className="eyebrow">Slug (URL-safe id)</div><input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} placeholder="bucket-hat" className="input" /></div>
              <div><div className="eyebrow">Name</div><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Bucket Hat" className="input" /></div>
              <div><div className="eyebrow">Description (optional)</div><textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="input" /></div>
              <p className="text-xs text-ink-3">New garments start with a single <strong className="text-ink">Front</strong> print location. Add Back, sleeves, or chest from the detail page.</p>
              <div className="rounded border border-brick/30 bg-brick-tint/60 px-3 py-2 text-[11px] text-brick-dark">
                <strong className="font-semibold text-brick">First time?</strong> The next page has a red <em>step-through</em> bar at the top — click each step to move through Mockup → Settings → Print areas → Colours → Sizes → Save.
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setCreating(false)} className="btn-outline flex-1 justify-center">Cancel</button>
              <button onClick={create} className="btn-gold flex-1 justify-center">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
